import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as blogService from '@/services/blogService';
import type { PostFilter, UpdatePostRequest, CreatePostRequest } from '@/types/blog';

export const BLOG_QUERY_KEYS = {
  all: ['blogPosts'] as const,
  lists: () => [...BLOG_QUERY_KEYS.all, 'list'] as const,
  list: (filter?: PostFilter) => [...BLOG_QUERY_KEYS.lists(), filter] as const,
  details: () => [...BLOG_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...BLOG_QUERY_KEYS.details(), id] as const,
  public: () => ['publicPosts'] as const,
  publicList: (limit?: number) => [...BLOG_QUERY_KEYS.public(), 'list', limit] as const,
  publicDetail: (slug: string) => [...BLOG_QUERY_KEYS.public(), 'detail', slug] as const,
};

// =============================================================================
// Admin hooks (protected routes)
// =============================================================================

export function useBlogPosts(filter?: PostFilter) {
  return useQuery(
    BLOG_QUERY_KEYS.list(filter),
    () => blogService.listPosts(filter),
    {
      keepPreviousData: true,
      staleTime: 30000,
    }
  );
}

export function useBlogPost(id: string | undefined) {
  return useQuery(
    BLOG_QUERY_KEYS.detail(id!),
    () => blogService.getPost(id!),
    { enabled: !!id }
  );
}

export function useCreatePost() {
  const queryClient = useQueryClient();
  return useMutation(
    (request: CreatePostRequest) => blogService.createPost(request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(BLOG_QUERY_KEYS.lists());
      },
    }
  );
}

export function useUpdatePost() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, request }: { id: string; request: UpdatePostRequest }) =>
      blogService.updatePost(id, request),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(BLOG_QUERY_KEYS.lists());
        queryClient.invalidateQueries(BLOG_QUERY_KEYS.detail(id));
      },
    }
  );
}

export function useDeletePost() {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => blogService.deletePost(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(BLOG_QUERY_KEYS.lists());
      },
    }
  );
}

export function usePublishPost() {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => blogService.publishPost(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(BLOG_QUERY_KEYS.lists());
        queryClient.invalidateQueries(BLOG_QUERY_KEYS.public());
      },
    }
  );
}

export function useUnpublishPost() {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => blogService.unpublishPost(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(BLOG_QUERY_KEYS.lists());
        queryClient.invalidateQueries(BLOG_QUERY_KEYS.public());
      },
    }
  );
}

export function useArchivePost() {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => blogService.archivePost(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(BLOG_QUERY_KEYS.lists());
        queryClient.invalidateQueries(BLOG_QUERY_KEYS.public());
      },
    }
  );
}

// =============================================================================
// Public hooks (for landing page, no auth required)
// =============================================================================

export function usePublishedPosts(limit?: number) {
  return useQuery(
    BLOG_QUERY_KEYS.publicList(limit),
    () => blogService.listPublishedPosts(limit),
    { staleTime: 60000 }
  );
}

export function usePostBySlug(slug: string) {
  return useQuery(
    BLOG_QUERY_KEYS.publicDetail(slug),
    () => blogService.getPostBySlug(slug),
    { enabled: !!slug, staleTime: 60000 }
  );
}
